import logoSvg from "./logo.svg?raw";
import cMarkSvg from "./c-mark.svg?raw";
import coverUrl from "./first-page-img.jpg";
import templateHtml from "./level-funded.html?raw";
import previewCss from "./proposal-preview.css?raw";
import { buildLevelFundedTables } from "./levelFundedTables.js";
import { contingenciesHtml } from "./contingenciesHtml.js";
import { renderMustache } from "./renderMustache.js";
import { renderAso } from "./renderAso.js";
import { isAsoTemplate } from "./asoModel.js";
import { renderRenewal } from "./renderRenewal.js";

const PLAN_OFFERING = {
    "partnership-cigna": "Centivo offers a health plan strategy tailored to your organization. Our Partnership Plan is offered alongside a traditional plan option with Cigna in one solution. The Partnership Plan delivers no deductible, free primary care, low copays, and discounted rates with high-quality providers—passing on savings to both employers and employees.",
    "partnership-only": "Centivo offers a health plan strategy tailored to your organization. The Partnership Plan delivers no deductible, free primary care, low copays, and discounted rates with high-quality providers—passing on savings to both employers and employees.",
};

const NETWORK_ACCESS = {
    "partnership-cigna": "Centivo’s high-performance network offers Partnership Plan members easy access to quality care through trusted providers, health systems, and specialists—available both in-person and virtually for all healthcare needs. Partnership Plan members can search for in-network doctors or facilities by logging into their account on the Centivo member portal or app. Those in the Cigna plan can visit the Cigna directory to find in-network care.",
    "partnership-only": "Centivo’s high-performance network offers Partnership Plan members easy access to quality care through trusted providers, health systems, and specialists—available both in-person and virtually for all healthcare needs. Partnership Plan members can search for in-network doctors or facilities by logging into their account on the Centivo member portal or app.",
};

export const DEFAULT_PHARMACY_COVERAGE = "Pharmacy benefits are fully integrated into the plan through a partnership with WriteWise and MedImpact. WriteWise serves as Centivo's capitated pharmacy partner, assuming full financial responsibility for all prescription drug expenses in exchange for a fixed per member per month (PMPM) fee—providing employers with predictable costs and eliminating pharmacy risk from the stop loss contract. MedImpact, a transparent independent PBM, administers all claims and manages the formulary, delivering affordability that reflects your financial and clinical goals. Members can search for covered drugs from the MedImpact formulary.";

const ASO_OPTION = "For employers who want more flexibility and complete transparency into your Medical and Rx benefit, we offer an administrative services only (ASO) option.";

const FIVE_YEAR = "Employers who switch to Centivo see meaningful results, and we expect the same for your organization. These projections reflect estimated total cost of care savings in one, three, and five years.";

const STOP_LOSS_PARAGRAPHS = [
    "With our level-funded plan, medical stop loss coverage is provided through Skyward Accident & Health, a division of Skyward Specialty Insurance, which is pre-integrated with our solution. With more than 25 years of industry experience, Skyward is led by a team of innovative specialty insurance leaders who rise above the status quo — bringing unconventional thinking and excellence in execution to stop loss solutions for self-insured plans. Skyward has been rigorously vetted by Centivo to meet the needs of our level-funded model, so you can be confident you have an experienced, specialized team working on your behalf.",
    "This is a self-insured proposal, and the expenses covered are governed by the stop loss contract. Pharmacy benefits are covered separately under a capitation arrangement with WriteWise and are not subject to the medical stop loss contract. Certain expenses may not be covered and may require additional client funding, such as expenses outside the contract period, shared savings costs, client exceptions, and care management fees. Consult the stop loss contract for full details.",
    "Client is responsible to for all claims up to the minimum attachment point based on SL contract enrollment. Refer to the stop loss requirements and contingencies in the Appendix below for further details.",
];

export const DEFAULT_STOP_LOSS_CONTRACT = STOP_LOSS_PARAGRAPHS.join("\n\n");

function escapeHtml(value) {
    return String(value ?? "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;");
}

function splitParagraphs(value, fallback) {
    const parts = String(value || "")
        .split(/\n\s*\n/)
        .map((part) => part.replace(/\s*\n\s*/g, " ").trim())
        .filter(Boolean);
    return parts.length ? parts : fallback;
}

function svgUri(svg) {
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
}

function absoluteAsset(url) {
    if (!url || typeof window === "undefined") return url || "";
    try {
        return new URL(url, window.location.href).href;
    } catch {
        return url;
    }
}

export function formatProposalDate(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    const [year, month, day] = raw.split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
    });
}

function dateLabel(value) {
    return formatProposalDate(value) || "[DATE]";
}

function buildToc(draft) {
    let page = 3;
    const toc = {
        executiveSummary: page,
        planOffering: page,
    };
    page += 1;
    toc.costComparison = page;
    page += 1;
    toc.rates = page;
    page += 1;
    toc.stopLossContractDetails = page;
    page += 1;
    toc.networkAccess = page;
    page += 1;
    toc.pharmacyCoverage = page;
    page += 1;
    if (draft.includeAsoOption) {
        toc.asoOption = page;
        page += 1;
    }
    if (draft.includeTotalCostOfCareSavings) {
        toc.totalCostOfCareSavings = page;
        page += 1;
    }
    toc.whatComesNext = page;
    page += 1;
    toc.appendix = page;
    page += 1;
    toc.appendixMembers = page;
    page += 1;
    toc.appendixStopLoss = page;
    return toc;
}

export function renderLevelFunded(draft) {
    const companyName = draft.companyName?.trim() || "[Company Name]";
    const variant = draft.planOfferingVariant === "partnership-only" ? "partnership-only" : "partnership-cigna";
    const toc = buildToc(draft);
    return renderMustache(templateHtml, {
        companyName,
        companyNameCaps: companyName.toUpperCase(),
        proposalDate: formatProposalDate(draft.proposalDate) || "Date TBD",
        assets: {
            logo: svgUri(logoSvg),
            cMark: svgUri(cMarkSvg),
            coverHero: absoluteAsset(coverUrl),
        },
        tables: buildLevelFundedTables(draft.data, draft.ratesTableVariant),
        pageBreak: '<div class="tpl-page-break-marker" data-page-break="true"></div>',
        planOfferingCopy: PLAN_OFFERING[variant],
        networkAccessCopy: NETWORK_ACCESS[variant],
        pharmacyCoverageParagraphs: splitParagraphs(draft.pharmacyCoverageCopy, [DEFAULT_PHARMACY_COVERAGE]),
        asoOptionCopy: ASO_OPTION,
        totalCostOfCareSavingsCopy: FIVE_YEAR,
        includeAsoOption: Boolean(draft.includeAsoOption),
        includeTotalCostOfCareSavings: Boolean(draft.includeTotalCostOfCareSavings),
        includeStandardPlansDisclosure: Boolean(draft.includeStandardPlansDisclosure),
        standardPlansDisclosure: "This proposal includes our recommended Standard Plans which most closely match what you currently offer. Benefits may differ slightly from your current plan designs.",
        cignaAffiliateDisclosure: "Centivo is an independent company and not an affiliate of Cigna. Any Cigna products and services are provided exclusively by or through operating subsidiaries of Cigna Corporation, including Cigna Health and Life Insurance Company. Cigna Intellectual Property, Inc. owns the Cigna name and other Cigna marks.",
        firmProposal: Boolean(draft.firmProposal),
        effectiveDateLabel: dateLabel(draft.effectiveDate),
        acceptanceDeadlineLabel: dateLabel(draft.acceptanceDeadline),
        showWriteWiseDisclosure: draft.ratesTableVariant === "ratesWithWriteWise",
        showCignaCareManagementNote: variant === "partnership-cigna",
        stopLossContractParagraphs: splitParagraphs(draft.stopLossContractCopy, STOP_LOSS_PARAGRAPHS),
        hasStopLoss: Boolean(String(draft.stopLossContingencies || "").trim()),
        stopLossHtml: contingenciesHtml(draft.stopLossContingencies),
        toc,
        footerPages: toc,
    });
}

export function proposalPreviewDocument(draft) {
    const body = isAsoTemplate(draft?.templateType)
        ? renderAso(draft)
        : draft?.templateType === "renewal"
            ? renderRenewal(draft)
            : renderLevelFunded(draft);
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${previewCss}</style></head><body><div class="proposal-preview-root">${body}</div></body></html>`;
}

function replaceFieldCopies(doc, field, className, paragraphs) {
    const existing = [...doc.querySelectorAll(`[data-aso-field="${field}"]`)];
    const host = existing[0]?.parentElement;
    if (!host) return;
    const anchor = existing[existing.length - 1].nextSibling;
    const focused = existing.some((node) => node.classList.contains("is-aso-editing"));
    existing.forEach((node) => node.remove());
    paragraphs.forEach((text) => {
        const paragraph = doc.createElement("p");
        paragraph.className = className;
        paragraph.setAttribute("data-aso-field", field);
        paragraph.textContent = text;
        if (focused) paragraph.classList.add("is-aso-editing");
        host.insertBefore(paragraph, anchor);
    });
}

/**
 * Write text-field edits into the open preview so the iframe does not reload.
 * @param {Document} doc
 * @param {Record<string, unknown>} patch
 */
export function applyDraftTextToPreview(doc, patch) {
    if (!doc) return;
    if ("companyName" in patch) {
        const name = String(patch.companyName || "").trim() || "[Company Name]";
        doc.querySelectorAll('[data-aso-field="companyName"]').forEach((node) => {
            node.textContent = name;
        });
        doc.querySelectorAll(".tpl-page-footer-center").forEach((node) => {
            const text = node.textContent || "";
            const pageAt = text.indexOf(" | ");
            if (pageAt < 0) return;
            const lead = text.trimStart().startsWith("Renewal Package for") ? "Renewal Package for" : "Proposal for";
            node.textContent = `${lead} ${name}${text.slice(pageAt)}`;
        });
    }
    if ("proposalDate" in patch) {
        const label = formatProposalDate(patch.proposalDate) || "Date TBD";
        doc.querySelectorAll('[data-aso-field="proposalDate"]').forEach((node) => {
            node.textContent = label;
        });
    }
    if ("stopLossContractCopy" in patch) {
        replaceFieldCopies(
            doc,
            "stopLossContract",
            "tpl-lf-editable",
            splitParagraphs(patch.stopLossContractCopy, STOP_LOSS_PARAGRAPHS),
        );
    }
    if ("pharmacyCoverageCopy" in patch) {
        replaceFieldCopies(
            doc,
            "pharmacyCoverage",
            "tpl-body-copy tpl-lf-editable",
            splitParagraphs(patch.pharmacyCoverageCopy, [DEFAULT_PHARMACY_COVERAGE]),
        );
    }
    if ("effectiveDate" in patch) {
        const label = dateLabel(patch.effectiveDate);
        doc.querySelectorAll('[data-aso-field="effectiveDate"]').forEach((node) => {
            node.textContent = label;
        });
    }
    if ("acceptanceDeadline" in patch) {
        const label = dateLabel(patch.acceptanceDeadline);
        doc.querySelectorAll('[data-aso-field="acceptanceDeadline"]').forEach((node) => {
            node.textContent = label;
        });
    }
    if ("stopLossContingencies" in patch) {
        const current = doc.querySelector('[data-aso-field="stopLossContingencies"]');
        if (!current) return;
        const focused = current.classList.contains("is-aso-editing");
        const next = doc.createElement(String(patch.stopLossContingencies || "").trim() ? "div" : "p");
        if (next.tagName === "DIV") {
            next.className = "tpl-prose md-body tpl-appendix-stop-loss";
            next.setAttribute("data-stop-loss-body", "");
            next.innerHTML = contingenciesHtml(patch.stopLossContingencies);
        } else {
            next.className = "tpl-body-copy";
            next.textContent = "Stop-loss supporting requirements and contingencies will appear here when a stop-loss document is added.";
        }
        next.setAttribute("data-aso-field", "stopLossContingencies");
        if (focused) next.classList.add("is-aso-editing");
        current.replaceWith(next);
    }
    const handled = new Set([
        "companyName",
        "proposalDate",
        "stopLossContractCopy",
        "pharmacyCoverageCopy",
        "effectiveDate",
        "acceptanceDeadline",
        "stopLossContingencies",
    ]);
    for (const [key, value] of Object.entries(patch)) {
        if (handled.has(key) || (value != null && typeof value === "object")) continue;
        doc.querySelectorAll(`[data-aso-field="${CSS.escape(key)}"]`).forEach((node) => {
            const empty = node.getAttribute("data-empty") || "";
            node.textContent = String(value ?? "").trim() || empty;
            const block = node.closest("[data-renewal-block]");
            if (block) block.hidden = !String(value ?? "").trim();
        });
    }
}
